// Cursor-tracked tilt + spotlight for product frames. Progressive enhancement:
// pointer:fine devices without prefers-reduced-motion only; everything else
// keeps the plain CSS hover. Sets CSS vars consumed by scoped styles:
//   --rx/--ry  rotateX/rotateY degrees toward the cursor
//   --mx/--my  spotlight position (percent) for the sheen layer
//
// Smoothness comes from a rAF lerp loop (values ease toward the cursor every
// frame) — NOT a CSS transition, which restarts on every mousemove and chops.
// Listeners attach to the frame's static parent (not the frame itself) so the
// tilted frame can't move out from under the cursor and flicker, and the
// pointer math uses the pre-tilt rect captured on mouseenter.
export function useTiltFrames(rootRef, selector = ".seo-frame", strength = 5) {
  onMounted(() => {
    const root = rootRef?.value;
    if (!root) return;
    if (!window.matchMedia("(pointer: fine)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const frames = root.querySelectorAll(selector);

    frames.forEach((frame) => {
      frame.classList.add("tilt-enabled");
      const host = frame.parentElement || frame;
      let rect = null;
      let raf = 0;
      const cur = { rx: 0, ry: 0 };
      const tgt = { rx: 0, ry: 0 };

      const apply = () => {
        frame.style.setProperty("--rx", `${cur.rx.toFixed(2)}deg`);
        frame.style.setProperty("--ry", `${cur.ry.toFixed(2)}deg`);
      };

      const tick = () => {
        cur.rx += (tgt.rx - cur.rx) * 0.14;
        cur.ry += (tgt.ry - cur.ry) * 0.14;
        const settled =
          Math.abs(tgt.rx - cur.rx) < 0.01 && Math.abs(tgt.ry - cur.ry) < 0.01;
        if (settled) {
          cur.rx = tgt.rx;
          cur.ry = tgt.ry;
        }
        apply();
        raf = settled ? 0 : requestAnimationFrame(tick);
      };

      const kick = () => {
        if (!raf) raf = requestAnimationFrame(tick);
      };

      host.addEventListener("mouseenter", () => {
        rect = frame.getBoundingClientRect();
        // JS-owned state: the frame's own :hover is unstable because the
        // tilted frame's edges move relative to the cursor (the spazz).
        frame.classList.add("is-tilting");
      });

      host.addEventListener("mousemove", (event) => {
        if (!rect) rect = frame.getBoundingClientRect();
        const px = (event.clientX - rect.left) / rect.width;
        const py = (event.clientY - rect.top) / rect.height;
        tgt.ry = (px - 0.5) * strength;
        tgt.rx = (0.5 - py) * strength;
        frame.style.setProperty("--mx", `${(px * 100).toFixed(1)}%`);
        frame.style.setProperty("--my", `${(py * 100).toFixed(1)}%`);
        kick();
      });

      host.addEventListener("mouseleave", () => {
        rect = null;
        tgt.rx = 0;
        tgt.ry = 0;
        frame.classList.remove("is-tilting");
        kick();
      });

      window.addEventListener(
        "scroll",
        () => {
          rect = null;
        },
        { passive: true },
      );
    });
  });
}
